import { useMemo, useState } from "preact/hooks"
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Button } from "../../../components/ui/button"
import { StatCard } from "../../../components/StatCard"
import { day_label, fmt_qty, num, STATUS_CLASS, STATUS_LABEL } from "./format"
import type { InventoryRow, InventoryView, StockStatus } from "./types"

const FILTERS: { value: StockStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "out", label: "Out" },
  { value: "low", label: "Low" },
  { value: "over", label: "Overstock" },
]

export function StatusBadge({ status }: { status: StockStatus }) {
  return <span class={`inline-block rounded px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[status]}`}>{STATUS_LABEL[status]}</span>
}

function InventoryTrend({ data }: { data: InventoryView }) {
  const points = data.trend.map((p: InventoryView["trend"][number]) => ({
    date: day_label(p.date),
    on_hand: num(p.on_hand),
    on_purchase_order: num(p.on_purchase_order),
    on_sales_order: num(p.on_sales_order),
  }))
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Units across all KeHE DCs</CardTitle>
      </CardHeader>
      <CardContent className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" fontSize={12} />
            <YAxis fontSize={12} tickFormatter={(v: number) => fmt_qty(v)} width={70} />
            <Tooltip formatter={(v: number) => fmt_qty(v)} />
            <Legend />
            <Line type="monotone" dataKey="on_hand" name="On hand" stroke="#2563eb" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="on_purchase_order" name="On order from you" stroke="#16a34a" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="on_sales_order" name="Ordered by stores" stroke="#ca8a04" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

function InventoryByDc({ data }: { data: InventoryView }) {
  const points = data.dcs.map((d: InventoryView["dcs"][number]) => ({
    dc: d.label,
    on_hand: num(d.on_hand),
    on_purchase_order: num(d.on_purchase_order),
  }))
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">On hand by DC</CardTitle>
      </CardHeader>
      <CardContent style={{ height: `${Math.max(256, points.length * 24 + 60)}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points} layout="vertical" margin={{ left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" fontSize={12} tickFormatter={(v: number) => fmt_qty(v)} />
            <YAxis type="category" dataKey="dc" fontSize={11} width={130} interval={0} />
            <Tooltip formatter={(v: number) => fmt_qty(v)} />
            <Legend />
            <Bar dataKey="on_hand" name="On hand" stackId="a" fill="#2563eb" />
            <Bar dataKey="on_purchase_order" name="On order" stackId="a" fill="#93c5fd" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

function InventoryTable({ rows }: { rows: InventoryRow[] }) {
  const [filter, set_filter] = useState<StockStatus | "all">("all")
  const shown = useMemo(() => rows.filter((r: InventoryRow) => filter === "all" || r.status === filter), [rows, filter])
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">By DC and item</CardTitle>
        <div class="flex gap-1">
          {FILTERS.map((f: { value: StockStatus | "all"; label: string }) => (
            <Button key={f.value} size="sm" variant={filter === f.value ? "default" : "outline"} onClick={() => set_filter(f.value)}>
              {f.label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="max-h-[520px] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>DC</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>UPC</TableHead>
              <TableHead className="text-right">On hand</TableHead>
              <TableHead className="text-right">Cases</TableHead>
              <TableHead className="text-right">On order</TableHead>
              <TableHead className="text-right">Store orders</TableHead>
              <TableHead className="text-right">Weeks of supply</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((r: InventoryRow) => (
              <TableRow key={`${r.dc_name}-${r.upc}`}>
                <TableCell>{r.dc_name}</TableCell>
                <TableCell>{r.item_name}</TableCell>
                <TableCell className="font-mono text-xs">{r.upc}</TableCell>
                <TableCell className="text-right">{fmt_qty(r.on_hand)}</TableCell>
                <TableCell className="text-right">{r.on_hand_cases === null ? "—" : num(r.on_hand_cases).toLocaleString("en-US")}</TableCell>
                <TableCell className="text-right">{fmt_qty(r.on_purchase_order)}</TableCell>
                <TableCell className="text-right">{fmt_qty(r.on_sales_order)}</TableCell>
                <TableCell className="text-right">{r.weeks_on_hand === null ? "—" : fmt_qty(r.weeks_on_hand)}</TableCell>
                <TableCell>
                  <StatusBadge status={r.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

export function InventoryTab({ data, className }: { data: InventoryView; className?: string }) {
  if (!data.snapshot_date) {
    return <p class="text-sm text-gray-600">No KeHE inventory pulled yet. It arrives with the daily KeHE reports sync.</p>
  }
  return (
    <div class={`space-y-4 ${className ?? ""}`}>
      <div class="grid gap-4 md:grid-cols-4">
        <StatCard title="On hand at KeHE" value={fmt_qty(data.on_hand)} detail={`units across ${data.dcs.length} DCs`} />
        <StatCard title="On order from you" value={fmt_qty(data.on_purchase_order)} detail="units on KeHE purchase orders" />
        <StatCard
          title="Out or low"
          value={`${data.out + data.low}`}
          detail={`${data.out} out, ${data.low} short of store orders or under 2 weeks`}
          warn={data.out + data.low > 0}
        />
        <StatCard title="Overstock" value={`${data.over}`} detail="DC × items over 12 weeks of supply" />
      </div>
      <div class="grid gap-4 lg:grid-cols-2">
        <InventoryTrend data={data} />
        <InventoryByDc data={data} />
      </div>
      <InventoryTable rows={data.rows} />
    </div>
  )
}
